import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import AdminSideNav from "./AdminSideNav";
import API_BASE_URL from "../../config";
import "./Dashboard.css";
import "../../Styles/Admin/Onboardingdata.css";

/* ── Empty form template (used for "Add New Organization") ── */
const EMPTY_FORM = {
  name: "",
  sector: "",
  years: "",
  description: "",
  address: "",
  state: "",
  city: "",
  website: "",
  permanent: "",
  contractual: "",
  coordName: "", coordDesignation: "", coordEmail: "", coordMobile: "",
  ceoName: "", ceoDesignation: "", ceoEmail: "", ceoMobile: "",
  hrName: "", hrDesignation: "", hrEmail: "", hrMobile: "",
  gstin: "",
  pin: "",
  purpose: "",
  heardFrom: "",
  confirmAccurate: false,
  confirmTerms: false,
};

const ENTRY_OPTIONS = [10, 20, 50, 100];

const SECTORS = [
  "Agriculture",
  "Automotive",
  "Banking & Financial Services (BFSI)",
  "Chemicals & Petrochemicals",
  "Construction & Infrastructure",
  "Consumer Goods & FMCG",
  "Education & Training",
  "Energy & Utilities",
  "Engineering & Industrial Manufacturing",
  "Environmental Services",
  "Food & Beverage",
  "Government & Public Sector",
  "Healthcare & Pharmaceuticals",
  "Hospitality & Tourism",
  "Information Technology (IT) & Software",
  "Insurance",
  "Logistics & Supply Chain",
  "Media & Entertainment",
  "Mining & Metals",
  "Manufacturing & Production",
  "Non-Profit & NGOs",
  "Oil & Gas",
  "Real Estate",
  "Retail & E-commerce",
  "Telecommunications",
  "Textiles & Apparel",
  "Transportation",
  "Research & Development (R&D)",
  "Aerospace & Defense",
  "Consulting & Professional Services",
  "Others",
];

const STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

/* Keeps an existing saved value selectable even if it isn't in the list */
const withCurrent = (list, current) =>
  current && !list.includes(current) ? [current, ...list] : list;

/* ── Pagination helper: 1 2 3 ... last ── */
const getPages = (current, total) => {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, "...", total];
  if (current >= total - 2) return [1, "...", total - 2, total - 1, total];
  return [1, "...", current, "...", total];
};

/* ── Small reusable label + input wrapper ── */
function Field({ label, required, children }) {
  return (
    <div className="onb-field">
      <label className="onb-label">
        {label}
        {required && <span className="onb-req"> *</span>}
      </label>
      {children}
    </div>
  );
}

/* ── Section heading with round icon ── */
function SectionHead({ title, children }) {
  return (
    <div className="onb-sec-head">
      <span className="onb-sec-icon">{children}</span>
      <h3>{title}</h3>
    </div>
  );
}

/* ── Contact block (Coordinator / CEO / HR) ── */
function ContactBlock({ title, prefix, form, set, readOnly }) {
  return (
    <div className="onb-sub-block">
      <h4 className="onb-sub-title">{title}</h4>
      <div className="onb-row-2">
        <Field label="Name" required>
          <input className="onb-input" placeholder="Full Name" required
            value={form[`${prefix}Name`]} readOnly={readOnly}
            onChange={set(`${prefix}Name`)} />
        </Field>
        <Field label="Designation" required>
          <input className="onb-input" placeholder="Designation" required
            value={form[`${prefix}Designation`]} readOnly={readOnly}
            onChange={set(`${prefix}Designation`)} />
        </Field>
      </div>
      <div className="onb-row-2">
        <Field label="Email ID" required>
          <input className="onb-input" type="email" placeholder="Email Id" required
            value={form[`${prefix}Email`]} readOnly={readOnly}
            onChange={set(`${prefix}Email`)} />
        </Field>
        <Field label="Mobile Number" required>
          <input className="onb-input" type="tel" placeholder="Mobile Number" required
            value={form[`${prefix}Mobile`]} readOnly={readOnly}
            onChange={set(`${prefix}Mobile`)} />
        </Field>
      </div>
    </div>
  );
}

export default function Onboardingdata() {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  /* mode: "list" | "add" | "edit" | "view" */
  const [mode, setMode] = useState("list");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);

  const readOnly = mode === "view";

  /* ── Load submissions from the backend ── */
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/onboarding`);
      if (!res.ok) throw new Error("Failed to load onboarding data");
      const rows = await res.json();
      setData(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error(err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Onboarding Datas | Outstanding Workplaces";
    fetchData();
  }, []);

  /* ── Search filter ── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) =>
      [row.id, row.name, row.sector, row.years, row.state, row.city, row.website]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [data, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * perPage;
  const pageRows = filtered.slice(startIdx, startIdx + perPage);

  const showingFrom = filtered.length === 0 ? 0 : startIdx + 1;
  const showingTo = Math.min(startIdx + perPage, filtered.length);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/admin/login"); // ⚠️ change if your login route is different
  };

  /* ── Open / close form ── */
  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setMode("add");
  };

  const openEdit = (row) => {
    setForm({ ...EMPTY_FORM, ...row });
    setEditingId(row.id);
    setMode("edit");
  };

  const openView = (row) => {
    setForm({ ...EMPTY_FORM, ...row });
    setEditingId(row.id);
    setMode("view");
  };

  const closeForm = () => {
    setMode("list");
    setEditingId(null);
  };

  /* ── Field change helpers ── */
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setCheck = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.checked }));

  const totalEmployees =
    (Number(form.permanent) || 0) + (Number(form.contractual) || 0);

  /* ── Save (add / edit) ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    try {
      if (mode === "add") {
        // the API expects the onboarding-form keys for new records
        const payload = {
          legalEntityName: form.name,
          industrySector: form.sector,
          yearsEstablished: form.years,
          orgDescription: form.description,
          headOfficeAddress: form.address,
          state: form.state,
          city: form.city,
          orgWebsite: form.website,
          permanentEmployees: form.permanent,
          contractualEmployees: form.contractual,
          coordinatorName: form.coordName,
          coordinatorDesignation: form.coordDesignation,
          coordinatorEmail: form.coordEmail,
          coordinatorMobile: form.coordMobile,
          ceoName: form.ceoName,
          ceoDesignation: form.ceoDesignation,
          ceoEmail: form.ceoEmail,
          ceoMobile: form.ceoMobile,
          hrHeadName: form.hrName,
          hrHeadDesignation: form.hrDesignation,
          hrHeadEmail: form.hrEmail,
          hrHeadMobile: form.hrMobile,
          gstinNumber: form.gstin,
          pinCode: form.pin,
          certificationPurpose: form.purpose,
          hearAboutUs: form.heardFrom,
          declarationAccuracy: form.confirmAccurate,
          declarationTerms: form.confirmTerms,
        };
        const res = await fetch(`${API_BASE_URL}/api/onboarding`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to add");
      } else if (mode === "edit") {
        const res = await fetch(`${API_BASE_URL}/api/onboarding/${editingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
        if (!res.ok) throw new Error("Failed to update");
      }
      await fetchData();
      closeForm();
    } catch (err) {
      console.error(err);
      alert("Could not save. Please try again.");
    }
  };

  /* ── Delete ── */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this organization? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/onboarding/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete");
      setData((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error(err);
      alert("Could not delete. Please try again.");
    }
  };

  /* ── Export to Excel (downloads an .xlsx of the records in the list) ── */
  const handleExportExcel = () => {
    if (filtered.length === 0) {
      alert("No records to export.");
      return;
    }

    const yesNo = (v) => (v ? "Yes" : "No");

    const rows = filtered.map((r) => ({
      "S.NO": r.id,
      "Organization Name": r.name ?? "",
      "Industry / Sector": r.sector ?? "",
      "Years Established In India": r.years ?? "",
      "Organization Description": r.description ?? "",
      "Head Office Address": r.address ?? "",
      "State / Union Territory": r.state ?? "",
      "City": r.city ?? "",
      "Organization Website": r.website ?? "",
      "Permanent Employees": Number(r.permanent) || 0,
      "Contractual Employees": Number(r.contractual) || 0,
      "Total Employee Strength":
        (Number(r.permanent) || 0) + (Number(r.contractual) || 0),
      "Coordinator Name": r.coordName ?? "",
      "Coordinator Designation": r.coordDesignation ?? "",
      "Coordinator Email": r.coordEmail ?? "",
      "Coordinator Mobile": r.coordMobile ?? "",
      "CEO / MD Name": r.ceoName ?? "",
      "CEO / MD Designation": r.ceoDesignation ?? "",
      "CEO / MD Email": r.ceoEmail ?? "",
      "CEO / MD Mobile": r.ceoMobile ?? "",
      "HR Head Name": r.hrName ?? "",
      "HR Head Designation": r.hrDesignation ?? "",
      "HR Head Email": r.hrEmail ?? "",
      "HR Head Mobile": r.hrMobile ?? "",
      "GSTIN Number": r.gstin ?? "",
      "PIN Code": r.pin ?? "",
      "Certification Purpose": r.purpose ?? "",
      "How Did You Hear About Us": r.heardFrom ?? "",
      "Accuracy Confirmed": yesNo(r.confirmAccurate),
      "Terms Accepted": yesNo(r.confirmTerms),
    }));

    const ws = XLSX.utils.json_to_sheet(rows);

    // Auto column width (capped so long text doesn't make a huge column)
    ws["!cols"] = Object.keys(rows[0]).map((key) => ({
      wch: Math.min(
        45,
        rows.reduce(
          (max, row) => Math.max(max, String(row[key] ?? "").length),
          key.length
        ) + 2
      ),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Onboarding Data");

    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `Onboarding_Data_${date}.xlsx`);
  };

  const pageTitle =
    mode === "add" ? "Onboarding Add Details"
    : mode === "edit" ? "Onboarding Edit Details"
    : mode === "view" ? "Onboarding View Details"
    : "Onboarding Datas";

  return (
    <>
      <AdminSideNav />

      <div className="dashboard-main-content">

        {/* ── TOP NAV ── */}
        <header className="admin-topnav">
          <div className="topnav-left">
            <h1>{pageTitle}</h1>
            <p>Forms and Submissions</p>
          </div>

          <div className="topnav-actions">
            <button type="button" className="onb-remainder-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#ffffff" strokeWidth="2" strokeDasharray="4 3" />
                <path d="M12 8v5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
                <circle cx="12" cy="16" r="1" fill="#ffffff" />
              </svg>
              Remainder
            </button>

            <button type="button" className="topnav-logout-btn" onClick={handleLogout}>
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16 17l5-5-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M21 12H9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Logout
            </button>
          </div>
        </header>

        {/* ══════════════ FORM (Add / Edit / View) ══════════════ */}
        {mode !== "list" && (
          <div className="dashboard-content onb-content">
            <form className="onb-form-card" onSubmit={handleSubmit}>

              {/* Card header: name (left) + close (right) */}
              <div className="onb-form-header">
                <h2>{form.name || "New Organization"}</h2>
                <button
                  type="button"
                  className="onb-close-btn"
                  onClick={closeForm}
                  aria-label="Close"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M5 5l14 14M19 5L5 19" stroke="#1A1E29" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Body: 2 x 2 grid, 50 / 50 */}
              <div className="onb-form-body">
                <div className="onb-form-grid">

                  {/* ── 1. Organization Details ── */}
                  <section className="onb-sec">
                    <SectionHead title="Organization Details">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" />
                        <path d="M12 11v5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="8" r="1" fill="#fff" />
                      </svg>
                    </SectionHead>

                    <div className="onb-panel">
                      <Field label="Organization Legal Entity Name" required>
                        <input className="onb-input" placeholder="Organization Legal Entity Name" required
                          value={form.name} readOnly={readOnly} onChange={set("name")} />
                      </Field>

                      <div className="onb-row-2">
                        <Field label="Industry / Sector" required>
                          <select className="onb-input onb-select" required
                            value={form.sector} disabled={readOnly} onChange={set("sector")}>
                            <option value="" disabled>Select sector</option>
                            {withCurrent(SECTORS, form.sector).map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Years Established in India" required>
                          <input className="onb-input" placeholder="Years Established in India" required
                            value={form.years} readOnly={readOnly} onChange={set("years")} />
                        </Field>
                      </div>

                      <Field label="Organization Description">
                        <textarea className="onb-input onb-textarea"
                          placeholder="Brief summary of the primary operational focus or mandate..."
                          value={form.description} readOnly={readOnly} onChange={set("description")} />
                      </Field>

                      <Field label="Head Office Address" required>
                        <textarea className="onb-input onb-textarea onb-textarea-sm" required
                          value={form.address} readOnly={readOnly} onChange={set("address")} />
                      </Field>

                      <div className="onb-row-2">
                        <Field label="State / Union Territory" required>
                          <select className="onb-input onb-select" required
                            value={form.state} disabled={readOnly} onChange={set("state")}>
                            <option value="" disabled>Select state</option>
                            {withCurrent(STATES, form.state).map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </Field>
                        <Field label="City" required>
                          <input className="onb-input" placeholder="City" required
                            value={form.city} readOnly={readOnly} onChange={set("city")} />
                        </Field>
                      </div>

                      <Field label="Organization Website">
                        <input className="onb-input" placeholder="https://example.com"
                          value={form.website} readOnly={readOnly} onChange={set("website")} />
                      </Field>
                    </div>
                  </section>

                  {/* ── 2. Employee Strength Declaration ── */}
                  <section className="onb-sec">
                    <SectionHead title="Employee Strength Declaration">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="6" r="2.2" stroke="#fff" strokeWidth="2" />
                        <path d="M12 8.5V19M6 13c0 3.5 2.7 6 6 6s6-2.5 6-6M8.5 12H6M18 12h-2.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </SectionHead>

                    <div className="onb-panel">
                      <div className="onb-row-2">
                        <Field label="Permanent Employees" required>
                          <input className="onb-input" type="number" min="0" placeholder="0" required
                            value={form.permanent} readOnly={readOnly} onChange={set("permanent")} />
                        </Field>
                        <Field label="Contractual Employees" required>
                          <input className="onb-input" type="number" min="0" placeholder="0" required
                            value={form.contractual} readOnly={readOnly} onChange={set("contractual")} />
                        </Field>
                      </div>

                      <div className="onb-total-strength">
                        <span className="onb-total-strength-title">Total Employee Strength</span>
                        <strong>{totalEmployees}</strong>
                        <small>Calculated automatically as permanent + contractual.</small>
                      </div>
                    </div>
                  </section>

                  {/* ── 3. Organization Contacts ── */}
                  <section className="onb-sec">
                    <SectionHead title="Organization Contacts">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M4 9l8-5 8 5M5 20h14M7 10v7M12 10v7M17 10v7" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </SectionHead>

                    <div className="onb-panel">
                      <ContactBlock title="Coordinator Details" prefix="coord" form={form} set={set} readOnly={readOnly} />
                      <div className="onb-dash" />
                      <ContactBlock title="CEO / MD / Head of Organization / Country Head" prefix="ceo" form={form} set={set} readOnly={readOnly} />
                      <div className="onb-dash" />
                      <ContactBlock title="HR Head Details" prefix="hr" form={form} set={set} readOnly={readOnly} />
                    </div>
                  </section>

                  {/* ── 4. Other Details ── */}
                  <section className="onb-sec">
                    <SectionHead title="Other Details">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="5" width="18" height="14" rx="2" stroke="#fff" strokeWidth="2" />
                        <circle cx="9" cy="11" r="2" stroke="#fff" strokeWidth="1.6" />
                        <path d="M14 10h4M14 14h4M6.5 16c.5-1.5 1.5-2 2.5-2s2 .5 2.5 2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
                      </svg>
                    </SectionHead>

                    <div className="onb-panel">
                      <h4 className="onb-sub-title onb-sub-title-spaced">GSTIN Information</h4>
                      <div className="onb-row-2">
                        <Field label="GSTIN Number" required>
                          <input className="onb-input" placeholder="GSTIN Number" required
                            value={form.gstin} readOnly={readOnly} onChange={set("gstin")} />
                        </Field>
                        <Field label="PIN Code" required>
                          <input className="onb-input" placeholder="PIN Code" required
                            value={form.pin} readOnly={readOnly} onChange={set("pin")} />
                        </Field>
                      </div>

                      <div className="onb-dash" />

                      <h4 className="onb-sub-title onb-sub-title-spaced">Certification Details</h4>
                      <Field label="Certification Purpose / Objective" required>
                        <textarea className="onb-input onb-textarea" placeholder="Describe the organizational goal..." required
                          value={form.purpose} readOnly={readOnly} onChange={set("purpose")} />
                      </Field>

                      <Field label="How did you hear about us?" required>
                        <input className="onb-input" placeholder="LinkedIn" required
                          value={form.heardFrom} readOnly={readOnly} onChange={set("heardFrom")} />
                      </Field>

                      <label className="onb-check">
                        <input type="checkbox" required
                          checked={!!form.confirmAccurate} disabled={readOnly}
                          onChange={setCheck("confirmAccurate")} />
                        <span>I confirm that all information provided in this application is accurate and complete to the best of my knowledge.</span>
                      </label>

                      <label className="onb-check">
                        <input type="checkbox" required
                          checked={!!form.confirmTerms} disabled={readOnly}
                          onChange={setCheck("confirmTerms")} />
                        <span>I agree to the terms and conditions, and consent to the processing of this application as per the organization's process.</span>
                      </label>
                    </div>
                  </section>

                </div>

                {/* Actions: hidden in View mode */}
                {!readOnly && (
                  <div className="onb-form-actions">
                    <button type="button" className="onb-btn-cancel" onClick={closeForm}>
                      Cancel
                    </button>
                    <button type="submit" className="onb-add-btn onb-btn-save">
                      {mode === "add" ? "Save Organization" : "Update Organization"}
                    </button>
                  </div>
                )}
              </div>
            </form>
          </div>
        )}

        {/* ══════════════ LIST ══════════════ */}
        {mode === "list" && (
          <div className="dashboard-content onb-content">

            {/* ── CARD ── */}
            <div className="onb-card">

              {/* Show Entries  +  Add New Organization (now inside the card) */}
              <div className="onb-toolbar">
                <label className="onb-entries-box">
                  <span>Show Entries :</span>
                  <select
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {ENTRY_OPTIONS.map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </label>

                <button type="button" className="onb-add-btn" onClick={openAdd}>
                  <span className="onb-add-plus">+</span>
                  Add New Organization
                </button>
              </div>

              {/* Search  |  Total Count + Export */}
              <div className="onb-card-top">
                <div className="onb-search-wrap">
                  <svg className="onb-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="7" stroke="#6b7280" strokeWidth="2" />
                    <path d="M20 20l-3.5-3.5" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <input
                    type="text"
                    className="onb-search-input"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>

                <div className="onb-card-top-right">
                  <div className="onb-total-box">Total Count : {data.length}</div>

                  <button type="button" className="onb-export-btn" onClick={handleExportExcel}>
                    <img src="/icons/excel.png" alt="Excel" width="20" height="20" />
                    Export To Excel
                  </button>
                </div>
              </div>

              {/* ── TABLE ── */}
              <div className="onb-table-wrap">
                <table className="onb-table">
                  <thead>
                    <tr>
                      <th>S.NO</th>
                      <th>Organization Name</th>
                      <th>Industry / Sector</th>
                      <th className="onb-center">Years Established In India</th>
                      <th>State / Union Territory</th>
                      <th>City</th>
                      <th>Organization Website</th>
                      <th className="onb-center">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={8} className="onb-empty">Loading...</td>
                      </tr>
                    ) : pageRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="onb-empty">No records found</td>
                      </tr>
                    ) : (
                      pageRows.map((row) => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>{row.name}</td>
                          <td>{row.sector}</td>
                          <td className="onb-center">{row.years}</td>
                          <td>{row.state}</td>
                          <td>{row.city}</td>
                          <td>{row.website}</td>
                          <td>
                            <div className="onb-actions">
                              {/* Edit */}
                              <button type="button" className="onb-action-btn onb-edit" title="Edit"
                                onClick={() => openEdit(row)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M4 20h4l10-10-4-4L4 16v4z" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
                                  <path d="M13 7l4 4" stroke="#fff" strokeWidth="2" />
                                </svg>
                              </button>
                              {/* Delete */}
                              <button type="button" className="onb-action-btn onb-delete" title="Delete"
                                onClick={() => handleDelete(row.id)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M5 7h14M10 7V4h4v3M7 7l1 13h8l1-13" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                              {/* View */}
                              <button type="button" className="onb-action-btn onb-view" title="View"
                                onClick={() => openView(row)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" stroke="#fff" strokeWidth="2" />
                                  <circle cx="12" cy="12" r="3" fill="#fff" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* ── FOOTER ── */}
              <div className="onb-card-footer">
                <span className="onb-showing">
                  Showing {showingFrom}–{showingTo} of {filtered.length} Companys
                </span>

                <div className="onb-pagination">
                  <button type="button" className="onb-page-arrow"
                    disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>
                    ‹
                  </button>

                  {getPages(safePage, totalPages).map((p, i) =>
                    p === "..." ? (
                      <span key={`dots-${i}`} className="onb-page-dots">...</span>
                    ) : (
                      <button key={p} type="button"
                        className={`onb-page-btn${safePage === p ? " active" : ""}`}
                        onClick={() => setPage(p)}>
                        {p}
                      </button>
                    )
                  )}

                  <button type="button" className="onb-page-arrow"
                    disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)}>
                    ›
                  </button>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </>
  );
}